import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8002") + "/api";
const TOKEN_KEY = "buymoreeasy_token";
const USER_KEY = "buymoreeasy_user";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem(USER_KEY);
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // Set axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  const fetchUser = useCallback(async () => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setToken(storedToken);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));
    } catch (error) {
      console.error("Error fetching user:", error);
      // Clear invalid token
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      delete axios.defaults.headers.common["Authorization"];
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password,
    });
    const { access_token, user: userData } = response.data;

    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    const { access_token, user: userData } = response.data;

    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  }, []);

  // ── Vérification email ──────────────────────────────────────────────────────

  const sendVerificationEmail = async (email) => {
    await axios.post(`${API_URL}/auth/send-verification-email`, { email });
  };

  const verifyEmail = async (email, code) => {
    const response = await axios.post(`${API_URL}/auth/verify-email`, { email, code });
    const { access_token, user: userData } = response.data;
    if (access_token) {
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
    }
    return response.data;
  };

  // ── Mot de passe oublié ─────────────────────────────────────────────────────

  const forgotPassword = async (email) => {
    await axios.post(`${API_URL}/auth/forgot-password`, { email });
  };

  const resetPassword = async (email, code, new_password) => {
    await axios.post(`${API_URL}/auth/reset-password`, { email, code, new_password });
  };

  const activateRole = async (roleType) => {
    const response = await axios.post(`${API_URL}/users/roles`, {
      role_type: roleType,
    });
    setUser(response.data);
    localStorage.setItem(USER_KEY, JSON.stringify(response.data));
    return response.data;
  };

  const hasRole = useCallback((roleType) => {
    if (!user?.roles) return false;
    return user.roles.some((r) => r.role_type === roleType);
  }, [user]);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      } catch (error) {
        console.error("Error refreshing user:", error);
      }
    }
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        activateRole,
        hasRole,
        refreshUser,
        sendVerificationEmail,
        verifyEmail,
        forgotPassword,
        resetPassword,
        isAuthenticated: !!user && !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
